import unescapedText from 'I18n/utils/unescapedText';
import { SearchInput } from 'foundations-components/transitional/components/SearchInput';
import { useDebounceValue } from 'marketplace-ui-common/hooks/useDebounceValue';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { AUTOCOMPLETE_QUERY as PUBLIC_AUTOCOMPLETE_QUERY } from '../../../__generated__/chirp/com/hubspot/query/understanding/mkpl/autocomp/rpc/MarketplaceAutocompletePublicRpc.dfc.new';
import { AUTOCOMPLETE_QUERY } from '../../../__generated__/chirp/com/hubspot/query/understanding/mkpl/autocomp/rpc/MarketplaceAutocompleteRpc.dfc.new';
import { usePersonalizationQuery } from '../../hooks/usePersonalizationQuery';
import { useSearchParams } from '../../hooks/useSearchParams';
import { LISTBOX_ID } from '../constants';
import { buildSearchUrl, navigateToSearchResults } from '../utils/navigation';
import { SearchDropdown } from './SearchDropdown';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const getOptionId = idx => `searchbar-dropdown-option-${idx}`;
export function SearchBar() {
  var _searchParams$get;
  const history = useHistory();
  const [open, setOpen] = useState(false);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const searchParams = useSearchParams();
  const [searchTerm, setSearchTerm] = useState((_searchParams$get = searchParams.get('search')) !== null && _searchParams$get !== void 0 ? _searchParams$get : '');
  const debouncedSearchTerm = useDebounceValue(searchTerm);
  const [activeIndex, setActiveIndex] = useState(-1);
  const debouncing = searchTerm !== debouncedSearchTerm;
  const {
    loading,
    data,
    error
  } = usePersonalizationQuery({
    app: AUTOCOMPLETE_QUERY,
    public: PUBLIC_AUTOCOMPLETE_QUERY,
    options: {
      skip: !debouncedSearchTerm,
      variables: {
        queryTerm: debouncedSearchTerm,
        fallbackLanguage: 'en',
        useSpellingCorrections: true,
        limit: 10
      }
    }
  });
  const options = useMemo(() => {
    var _data$result$autocomp, _data$result;
    const opt = (_data$result$autocomp = data === null || data === void 0 || (_data$result = data.result) === null || _data$result === void 0 ? void 0 : _data$result.autocompleteResultItems) !== null && _data$result$autocomp !== void 0 ? _data$result$autocomp : [];
    if (opt.length > 0) return opt;
    return [{
      resultType: 'QUERY',
      term: searchTerm,
      language: 'en',
      sourceTerm: searchTerm,
      category: []
    }];
  }, [data, searchTerm]);
  const closeDropdown = () => {
    setOpen(false);
    setActiveIndex(-1);
  };
  const handleResultSelect = item => {
    var _inputRef$current;
    switch (item.resultType) {
      case 'QUERY':
        setSearchTerm(item.term || '');
        break;
      case 'LISTING':
      case 'CATEGORY':
      case 'COLLECTION':
      default:
        setSearchTerm('');
        break;
    }
    navigateToSearchResults(item, history);
    closeDropdown();
    (_inputRef$current = inputRef.current) === null || _inputRef$current === void 0 || _inputRef$current.blur();
  };
  const moveDown = () => {
    setActiveIndex(prev => (prev + 1) % options.length);
  };
  const moveUp = () => {
    setActiveIndex(prev => (prev - 1 + options.length) % options.length);
  };
  const handleKeyDown = e => {
    if (!open || !options.length) return;
    const handledKeys = new Set(['ArrowDown', 'ArrowUp', 'Enter', 'Escape']);
    if (handledKeys.has(e.key)) {
      e.preventDefault();
    }
    switch (e.key) {
      case 'ArrowDown':
        moveDown();
        break;
      case 'ArrowUp':
        moveUp();
        break;
      case 'Enter':
        if (activeIndex >= 0 && activeIndex < options.length) {
          handleResultSelect(options[activeIndex]);
        } else {
          var _inputRef$current2;
          history.push(buildSearchUrl(searchTerm));
          (_inputRef$current2 = inputRef.current) === null || _inputRef$current2 === void 0 || _inputRef$current2.blur();
        }
        break;
      case 'Escape':
        closeDropdown();
        break;
      default:
        break;
    }
  };
  const handleChange = e => {
    setSearchTerm(e.target.value);
    setOpen(true);
  };
  useEffect(() => {
    const handleOutsideClick = e => {
      var _containerRef$current;
      if ((_containerRef$current = containerRef.current) !== null && _containerRef$current !== void 0 && _containerRef$current.contains(e.target)) return;
      closeDropdown();
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);
  useEffect(() => {
    setActiveIndex(-1);
  }, [options === null || options === void 0 ? void 0 : options.length, open]);
  return /*#__PURE__*/_jsxs("div", {
    ref: containerRef,
    style: {
      width: '100%',
      position: 'relative'
    },
    onKeyDown: handleKeyDown,
    children: [/*#__PURE__*/_jsx(SearchInput, {
      "data-test-id": "storefront-search-input",
      inputRef: inputRef,
      placeholder: unescapedText('marketplaceStorefront.searchBar.placeholder'),
      "aria-label": unescapedText('marketplaceStorefront.searchBar.placeholder'),
      "aria-controls": searchTerm && open ? LISTBOX_ID : undefined,
      "aria-expanded": !!(searchTerm && open),
      "aria-activedescendant": searchTerm && open && activeIndex >= 0 ? getOptionId(activeIndex) : undefined,
      value: searchTerm,
      onChange: handleChange
    }), searchTerm && open && /*#__PURE__*/_jsx(SearchDropdown, {
      activeIndex: activeIndex,
      error: !!error,
      loading: debouncing || loading,
      onResultSelect: handleResultSelect,
      options: options,
      searchTerm: searchTerm,
      setActiveIndex: setActiveIndex
    })]
  });
}